/** 两个初始化阶段共用同一物理脚本。 */
export type AnalyticsScript = 'sensors' | 'classic';

/** 单文件先执行神策源码，再建立 Classic 命令入口并执行观鹿源码。 */
const SCRIPT_URL = '/d7735a.js';

/** 一个脚本的等待结果及可撤销监听。 */
interface ScriptEntry {
  promise: Promise<void>;
  cancel: () => void;
}

/** 文档销毁后保持终态，禁止迟到回调或新调用恢复加载。 */
interface DocumentScripts {
  disposed: boolean;
  entries: Map<AnalyticsScript, ScriptEntry>;
}

/** 按文档保存有限状态，避免跨文档复用已销毁的脚本。 */
const documents = new WeakMap<Document, DocumentScripts>();

/**
 * 判断单文件是否已接管 Classic 命令队列。
 * @returns 已暴露可调用 Classic 入口且队列不再是 bootstrap 数组时返回 true。
 */
function isClassicReady(): boolean {
  const candidate: unknown = (window as unknown as { snowplow?: unknown }).snowplow;
  if (typeof candidate !== 'function') return false;
  const queue: unknown = (candidate as { q?: unknown }).q;
  return Boolean(
    queue && !Array.isArray(queue) && typeof (queue as { push?: unknown }).push === 'function',
  );
}

/** 复用 pending/resolved Promise；Classic 阶段等待身份后检查已下载的单文件。 */
export function loadAnalyticsScript(kind: AnalyticsScript): Promise<void> {
  if (typeof document === 'undefined')
    return Promise.reject(new Error('analytics_document_missing'));
  if (kind === 'classic') {
    if (isClassicReady()) return Promise.resolve();
    return loadAnalyticsScript('sensors').then(() => {
      if (!isClassicReady()) throw new Error('analytics_classic_missing');
    });
  }
  const ownerDocument = document;
  let state = documents.get(ownerDocument);
  if (!state) {
    state = { disposed: false, entries: new Map() };
    documents.set(ownerDocument, state);
  }
  if (state.disposed) return Promise.reject(new Error('analytics_script_loader_disposed'));
  const existing = state.entries.get(kind);
  if (existing) return existing.promise;

  // 先登记状态再操作DOM，兼容同步触发load的测试或宿主环境。
  let resolve!: () => void;
  let reject!: (error: Error) => void;
  const promise = new Promise<void>((onResolve, onReject) => {
    resolve = onResolve;
    reject = onReject;
  });
  let settled = false;
  let script: HTMLScriptElement | undefined;
  const entry: ScriptEntry = { promise, cancel };
  state.entries.set(kind, entry);

  /** 只解除本站点附加的监听，保留旧入口自己的处理器。 */
  function detach(): void {
    script?.removeEventListener('load', onLoad);
    script?.removeEventListener('error', onError);
  }
  /** load仅表示脚本完成；神策初始化及身份由独立门禁确认。 */
  function onLoad(): void {
    if (settled || state!.disposed) return;
    settled = true;
    detach();
    resolve();
  }
  /** 明确失败清除节点与单例记录，交给编排器决定重试时机。 */
  function onError(): void {
    if (settled || state!.disposed) return;
    settled = true;
    detach();
    script?.remove();
    state!.entries.delete(kind);
    reject(new Error('analytics_script_load_failed'));
  }
  /** 文档销毁取消等待，但不通过删除pending节点触发第二次加载。 */
  function cancel(): void {
    detach();
    if (settled) return;
    settled = true;
    reject(new Error('analytics_script_loader_disposed'));
  }

  try {
    const url = SCRIPT_URL;
    // 集成切换前复用layout的相同URL节点，不能并行插入覆盖SDK全局对象。
    script = Array.from(ownerDocument.querySelectorAll<HTMLScriptElement>('script[src]')).find(
      (candidate) => candidate.src === new URL(url, ownerDocument.baseURI).href,
    );
    const needsInsertion = !script;
    script ??= ownerDocument.createElement('script');
    script.setAttribute('data-zl-analytics', kind);
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    if (needsInsertion) {
      script.async = true;
      script.src = url;
      ownerDocument.head.appendChild(script);
    }
  } catch {
    onError();
  }
  return promise;
}

/** 页面销毁时取消本站点监听并忽略迟到回调。 */
export function disposeAnalyticsScriptLoader(): void {
  if (typeof document === 'undefined') return;
  let state = documents.get(document);
  if (!state) {
    state = { disposed: true, entries: new Map() };
    documents.set(document, state);
  }
  state.disposed = true;
  for (const entry of Array.from(state.entries.values())) entry.cancel();
}
